import { html } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import { style } from './zui-shell-nav-css.js';
import { classMap } from 'lit/directives/class-map.js';
import { cache } from 'lit/directives/cache.js';
import { ZuiShellBase } from './zui-shell-base';
import { navCollapseChangeEventName } from './utils/shell-state';
import '@zywave/zui-icons';
import type { ZuiShellNavItem } from './zui-shell-nav-item.js';
import type { PropertyValues } from 'lit';

/**
 * The Shell collapsible side nav that houses the application's primary navigation. On medium screen sizes, the side nav automatically collapses to give users more screen real estate. Users can choose to expand it if they prefer.
 *
 * @element zui-shell-nav
 *
 * @slot - Default, unnamed slot; for inserting `<zui-shell-nav-item>` and `<zui-shell-context-switcher>` elements into `<zui-shell-nav>`
 * 
 * @cssprop [--zui-shell-nav-width=(variable)] - Width of `<zui-shell-nav>` when expanded
 * @cssprop [--zui-shell-nav-width-collapsed=3.75rem] - Width of `<zui-shell-nav>` when collapsed
 
 * @event {CustomEvent} navcollapsechange - fired when nav is collapsed or expanded
 */
export class ZuiShellNav extends ZuiShellBase {
  /**
   * Indicates whether the side nav is open or not on mobile
   */
  @property({ type: Boolean, reflect: true }) open = false;

  /**
   * Indicates when the side nav is collapsed
   */
  @property({ type: Boolean, reflect: true }) collapsed: boolean = this._isNavCollapsed;

  /**
   * (Optional) For customizing the text for the collapse button at the bottom of the side nav
   */
  @property({ type: String, attribute: 'collapse-label' }) collapseLabel = 'Collapse';
  private _navItems: ZuiShellNavItem[] = [];

  @query('.utility-items')
  private _utilityItemsWrapper: HTMLDivElement;

  @queryAssignedElements({ slot: 'utility' })
  private _utilitySlottedEls: Array<ZuiShellNavItem>;

  static get styles() {
    return [super.styles, style];
  }

  #isInitializing = true;

  constructor() {
    super();

    this._observeAppNameChange = true;
    this._observePageScrollChange = true;
    this._observeHasAppBar = true;
    this._observeMobileNavOpenChange = true;
    this._observeBannerHeightChange = true;
  }

  connectedCallback() {
    super.connectedCallback();
    this._navItems = Array.from(this.querySelectorAll('zui-shell-nav-item'));
    this._hasShellNav = true;
    // TODO: remove this and any other uses of `open` from zui-shell-nav-item
    for (const navItem of this._navItems) {
      navItem.open = this.open;
    }

    this._storeElement(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._hasShellNav = false;
    this._removeElement(this);
  }

  updated(changedProps: PropertyValues) {
    super.updated(changedProps);

    if (changedProps.has('open')) {
      for (const navItem of this._navItems) {
        navItem.open = this.open;
      }
      this._isMobileNavOpen = this.open;

      this.#hideBodyScroll();
    }
    if (changedProps.has('collapsed') && !this.#isInitializing) {
      this.#onCollapsedChange();
    }
    this.#isInitializing = false;
  }

  firstUpdated() {
    this.#toggleUtilityItems();
  }

  protected onScreenBreakpointChange() {
    super.onScreenBreakpointChange();
    if (this.open && !this._isMobile) {
      this.open = false;
    }
    this._tabletNavCollapseOverride = undefined;
    this.collapsed = this._isNavCollapsed;
  }

  protected onMobileNavOpenChange() {
    this.open = this._isMobileNavOpen;
  }

  render() {
    return html` ${cache(this._isMobile ? this.#renderDrawerNav() : this.#renderStandardNav())} `;
  }

  #renderDrawerNav() {
    return html`
      <nav id="navigation" class="${classMap({ 'no-appbar': !this._hasAppBar, 'no-app-name': !this._appName })}">
        <div class="nav-wrapper">
          <div class="close-menu" @click="${() => (this.open = !this.open)}"></div>
          <div class="is-drawer">
            <div class="app-name"><slot name="app">${this._appName}</slot></div>
            <div class="drawer-menu" @navitemclick="${() => (this.open = false)}"> <slot></slot> </div>
            ${this.#renderUtilityItems()}</div>
          </div>
        </div>
      </nav>
    `;
  }

  #renderStandardNav() {
    return html`
      <nav
        id="navigation"
        class="${classMap({
          scrolled: this._state.pageScroll,
          'no-appbar': !this._hasAppBar,
          'no-app-name': !this._appName,
        })}"
      >
        <div class="nav-wrapper"
          ><div class="is-standard"> <slot></slot> </div>
          ${this.#renderUtilityItems()}${this.#renderCollapseButton()}</div
        >
      </nav>
    `;
  }

  #renderUtilityItems() {
    return html`
      <div class="utility-items"> <slot name="utility" @slotchange="${this.#toggleUtilityItems}"></slot> </div>
    `;
  }

  #renderCollapseButton() {
    return html`
      <div class="collapse" @click="${() => (this.collapsed = !this.collapsed)}">
        <zui-icon icon="zui-chevron-left"></zui-icon>
        ${this.collapsed ? html`` : html` ${this.collapseLabel} `}
      </div>
    `;
  }

  /**
   * Toggle nav `collapsed` state
   */
  toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  #onCollapsedChange() {
    if (this._isTablet) {
      this._tabletNavCollapseOverride = this.collapsed;
    } else {
      this._storedIsNavCollapsed = this.collapsed;
    }
    /**
     * @ignore
     */
    this.dispatchEvent(
      new CustomEvent(navCollapseChangeEventName, {
        detail: this._isNavCollapsed,
        bubbles: true,
        composed: true,
      })
    );

    // if the nav is collapsed, all nav items must not be expanded
    if (this.collapsed) {
      const expandedNavItems = this.querySelectorAll('zui-shell-nav-item[expanded]');
      for (const navItem of expandedNavItems) {
        (navItem as ZuiShellNavItem).toggleExpanded();
      }
    }
  }

  #hideBodyScroll() {
    document.body.style.overflow = this.open ? 'hidden' : '';
  }

  // Toggle the display of the utility items wrapper if there are any utility items
  async #toggleUtilityItems() {
    const utilityEls = await this._utilitySlottedEls;
    if (utilityEls.length > 0) {
      this._utilityItemsWrapper.classList.toggle('show', true);
    } else {
      this._utilityItemsWrapper.classList.toggle('show', false);
    }
    this.requestUpdate();
  }
}

window.customElements.define('zui-shell-nav', ZuiShellNav);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-nav': ZuiShellNav;
  }
}
